import type { EditAreaTemplateApplication } from '@repo/api-contracts';
import { ArrowLeft } from 'lucide-react';
import { type ReactElement, useEffect, useState } from 'react';
import { Link } from 'react-router';
import { EditAreaDraftProvider, useLatestEditAreaQuery } from '@/entities/edit-area';
import { useProductDetailQuery, useRefreshProductDetail } from '@/entities/product';
import { Button, PageHeader } from '@/shared/ui';
import { ProductEditAreaSession } from './product-edit-area-session';

interface ProductEditAreaWorkspaceProps {
  readonly productId: string;
}

export const ProductEditAreaWorkspace = ({
  productId,
}: ProductEditAreaWorkspaceProps): ReactElement => {
  const product = useProductDetailQuery(productId);
  const definition = useLatestEditAreaQuery(productId, true);
  const refreshProduct = useRefreshProductDetail();
  const [hasEntered, setHasEntered] = useState(false);
  // 템플릿·복사 결과 — 상품(실측 종류·실측)을 다시 읽은 뒤 초안에 넣는다. 실측 종류가 바뀌면 초안 store 가 새로 만들어지므로
  // 그 뒤에 세션이 넣어야 기하가 사라지지 않는다.
  const [pending, setPending] = useState<EditAreaTemplateApplication | null>(null);
  const [applied, setApplied] = useState<{
    readonly application: EditAreaTemplateApplication;
    readonly sourceLabel: string;
  } | null>(null);
  const handleApplied = async (
    application: EditAreaTemplateApplication,
    sourceLabel: string,
  ): Promise<void> => {
    await refreshProduct(productId);
    setPending(application);
    setApplied({ application, sourceLabel });
  };
  const initialReady =
    product.data !== undefined &&
    definition.data !== undefined &&
    !product.isFetching &&
    !definition.isFetching &&
    !product.isError &&
    !definition.isError;
  useEffect(() => {
    if (initialReady) setHasEntered(true);
  }, [initialReady]);
  if (
    product.isPending ||
    definition.isPending ||
    (!hasEntered && (product.isFetching || definition.isFetching || initialReady))
  )
    return <p role="status">상품과 편집영역을 불러오는 중…</p>;
  if (
    product.data === undefined ||
    definition.data === undefined ||
    (!hasEntered && (product.isError || definition.isError))
  )
    return (
      <section role="alert">
        <p>상품 또는 편집영역을 불러오지 못했습니다. 기존 정의를 확인한 후 편집할 수 있습니다.</p>
        <Button
          type="button"
          onClick={() => {
            void product.refetch();
            void definition.refetch();
          }}
        >
          다시 불러오기
        </Button>
      </section>
    );
  const measurement =
    product.data.widthMm === null || product.data.heightMm === null
      ? null
      : { widthMm: product.data.widthMm, heightMm: product.data.heightMm };
  // 원통 상품의 widthMm 는 지름이다(기본정보 폼 '지름 (mm)' 칸과 같은 이름으로 안내한다).
  const measurementFields = product.data.measurementKind === 'CYLINDER' ? '지름·높이' : '넓이·높이';
  return (
    <div className="space-y-5">
      <PageHeader
        title={`${product.data.name} · 편집영역 설정`}
        description="상품 추출하기로 표면 설정 → 사진 위에서 인쇄 영역 그리기 → 인스펙터로 미세 조정 → 확인·저장"
        actions={
          <Link
            to={`/products/editor/${productId}`}
            className="inline-flex h-10 items-center gap-2 rounded-full border border-input bg-background px-5 text-sm font-medium whitespace-nowrap transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none [&_svg]:size-4"
          >
            <ArrowLeft aria-hidden="true" />
            상품 수정으로 돌아가기
          </Link>
        }
      />
      {product.isError || definition.isError ? (
        <p role="alert">정보 갱신에 실패했습니다. 현재 편집 입력은 유지됩니다.</p>
      ) : null}
      {measurement === null ? (
        <p role="alert" className="rounded-lg border p-4 text-sm">
          상품 실측이 없습니다.{' '}
          <Link to={`/products/editor/${productId}`} className="underline">
            상품 수정
          </Link>
          에서 실측 규격({measurementFields})을 등록하면 상품 추출과 저장을 할 수 있습니다. 인쇄
          영역 입력과 사진 관리는 지금도 가능합니다.
        </p>
      ) : null}
      <EditAreaDraftProvider
        key={`${productId}:${product.data.measurementKind}`}
        productId={productId}
        colorOptionIds={product.data.colorOptions.map((color) => color.id)}
        serverState={definition.data}
        measurement={measurement}
        measurementKind={product.data.measurementKind}
      >
        <ProductEditAreaSession
          productName={product.data.name}
          colors={product.data.colorOptions}
          pendingTemplate={pending}
          appliedTemplate={applied}
          onAppliedDismiss={() => setApplied(null)}
          onTemplateConsumed={() => setPending(null)}
          onTemplateApplied={(application, sourceLabel) => {
            void handleApplied(application, sourceLabel);
          }}
        />
      </EditAreaDraftProvider>
    </div>
  );
};
